'use client';

/**
 * The `bankAccount` field's editor — **one popover holding the whole account**
 * (VALE-1211, `components/attributes/CLAUDE.md` §3).
 *
 * A bank account is one real-world thing, and this package was spending up to
 * five rows on it. `attribute-name-editor.tsx` is the model this is built from
 * directly, for the reason that editor states: parts that are only meaningful
 * together belong in one popover, on the shared Escape-reverts /
 * dismiss-commits contract.
 *
 * ## What it shows at rest, and what it does not
 *
 * {@link formatBankAccountSummary} is the read-out: **the BSB and the account
 * number**, the two facts a reader is checking, with the verified state carried
 * beside them as the row's `badge` ({@link bankAccountVerifiedBadge}). The
 * account name is deliberately absent — it is typed once and never re-read, and
 * spending the row's width on it would push the two numbers into an ellipsis.
 *
 * **A partly-captured account reads as what it is.** A BSB recorded from a
 * statement days before anyone has the account number is a normal state during
 * discovery, so the summary labels the lone part (`BSB 062-000`) rather than
 * rendering the row empty or padding it with a dash. Hiding a value that is
 * genuinely on the record is the one option here that can mislead.
 *
 * ## The BSB carries its separator, and that is a deliberate widening
 *
 * `062-000` is how a BSB is written everywhere outside this database — on a
 * statement, on a deposit slip, in the letter the firm sends — so the editor
 * formats it as the reader types and the read-out formats it at rest. Vale still
 * **stores six digits**: every spec's `toPatch` strips the separator, exactly as
 * the firm trust account's row already did.
 *
 * That means the Contacts field now accepts hyphenated input it used to refuse,
 * which is a real behaviour change and is the point. Its old rule (`/^\d{6}$/`)
 * was correct while the value was free-typed into a bare text box; refusing a
 * separator **this editor itself inserts** would be a field refusing its own
 * output. See `contact-attribute-specs.ts`'s `bankAccountField`.
 */

import { ShieldCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { formatBSB } from '@/lib/formatters/australian-formatters';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_COMPOSITE_PART,
  ATTRIBUTE_COMPOSITE_PART_CONTROL,
  ATTRIBUTE_COMPOSITE_PART_INLINE,
  ATTRIBUTE_COMPOSITE_PART_LABEL,
  ATTRIBUTE_COMPOSITE_PARTS,
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_POPOVER_COLLISION_PADDING,
  ATTRIBUTE_POPOVER_PANEL,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import { attributePlaceholder } from './attribute-placeholder';
import { AttributeSegmentedBoolean } from './attribute-segmented-boolean';
import type { AttributeBankAccount, AttributeDraft } from './attribute-types';
import { useAttributePopoverCommit } from './use-attribute-popover-commit';

/**
 * Six digits at most, hyphenated after the third — `062-000` from `062000`,
 * and `062-0` from `0620` while it is still being typed.
 *
 * Distinct from {@link formatBSB}, which this module uses for the *read-out*:
 * that one formats a stored, complete BSB and leaves anything else alone, which
 * is right for a value the record already holds and wrong for one arriving a
 * keystroke at a time — it would insert the separator only on the sixth digit
 * and make the field jump under the cursor.
 */
export function formatBsbInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 6);
  if (digits.length <= 3) return digits;
  return `${digits.slice(0, 3)}-${digits.slice(3)}`;
}

/** The six digits Vale stores, from whatever the reader typed. */
export function bsbDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '').slice(0, 6);
}

/**
 * **An account with every part blank is `null`, not an object of empty
 * strings** — the same rule the address and name editors apply, so an *"is
 * anything captured here?"* read does not see an object and answer yes.
 *
 * `isVerified` is excluded from the emptiness test on purpose: a bare
 * `isVerified: false` is the *absence* of a verification, not a captured fact,
 * so an account whose only content is that flag is still an empty account. A
 * `true` is different, and is kept by the `some` below.
 */
function normalise(account: AttributeBankAccount): AttributeBankAccount | null {
  const hasText = [
    account.bsb,
    account.accountNumber,
    account.accountName,
    account.verificationNote
  ].some((part) => (part ?? '').trim().length > 0);
  return hasText || account.isVerified === true ? account : null;
}

/**
 * **The row's read-out — the BSB and the account number, in that order.**
 *
 * Exported so every spec that renders an account binds to one definition rather
 * than restating the join (root `CLAUDE.md`, *"bind to the source of truth"*).
 * Three surfaces render this row and a `·`-joined summary must never mean two
 * different things.
 *
 * Each part is labelled **only when it is alone**: `062-000 · 12345678` needs no
 * captions, because the order and the shapes say which is which, but a lone
 * number is genuinely ambiguous between the two.
 */
export function formatBankAccountSummary(
  account: AttributeBankAccount | null
): string {
  if (!account) return '';
  const bsb = bsbDigits(account.bsb);
  const number = (account.accountNumber ?? '').trim();
  if (bsb && number) return `${formatBSB(bsb)} · ${number}`;
  if (bsb) return `BSB ${formatBSB(bsb)}`;
  if (number) return `Account ${number}`;
  return '';
}

/**
 * **What the row puts on the clipboard** — the two numbers, space-separated and
 * unpunctuated except for the BSB's own separator.
 *
 * Not {@link formatBankAccountSummary}: the `·` is a reading aid, and the reader
 * copying this row is pasting into a banking portal or a payment form, where a
 * middot is a validation error. The account name is left out for the same reason
 * it is left out of the summary — it goes in a different box.
 */
export function bankAccountCopyText(
  account: AttributeBankAccount | null
): string {
  if (!account) return '';
  const bsb = bsbDigits(account.bsb);
  const number = (account.accountNumber ?? '').trim();
  return [bsb ? formatBSB(bsb) : '', number].filter(Boolean).join(' ');
}

/**
 * **The verified mark a spec hands to `AttributeSpec.badge` — the tick alone.**
 *
 * It was a full `Verified` chip and is now the icon on its own (VALE-1247).
 * Two reasons, and the second is the one that forced it. The row already says
 * `Bank account`, so the word `Verified` beside an account number is the
 * shortest possible sentence spent on a fact a tick states; and the read-out
 * renders a *string* badge inside its own neutral chip, so a caller supplying
 * a chip of its own got a green chip inside a grey one — which is what this
 * looked like on the review surfaces. That wrapper is now scoped to plain text
 * (see `AttributeReadOut`), and this is what a caller-drawn badge should be:
 * a bare mark, sized to sit inside the value's own target.
 *
 * `aria-label` + `role="img"` because the word is what carried the meaning to a
 * screen reader and the icon has to carry it now.
 *
 * A function returning a node rather than a component, because the specs that
 * need it (`contact-attribute-specs.ts`) are `.ts` modules that cannot hold JSX
 * — and because `badge` takes a node, not an element type.
 *
 * **Shown only when the account is verified** (VALE-1211). This is a deliberate
 * departure from §1.8's *"an unticked box is indistinguishable from a question
 * nobody answered"*, and the departure is scoped to the read-out: most accounts
 * are unverified for most of their life, so a `Not verified` chip on every row
 * would be a wall of grey saying nothing. The distinction §1.8 protects is
 * preserved where it is actually asked — **inside the popover**, where the
 * verified part is the treatment's own `Yes` / `No` pair and an unanswered flag
 * still reads as unanswered.
 */
export function bankAccountVerifiedBadge(): React.ReactNode {
  return (
    <ShieldCheck
      /*
        `size-4` and `block`, not `h-3.5` inline (VALE-1247). An inline SVG sits
        on the text baseline, so it hangs below the middle of the line it is
        beside however the box around it is aligned — which is what left the
        tick reading low against the account number. `block` takes it off the
        baseline so the flex box's `items-center` is what positions it, and 16px
        against 13px text puts the glyph's own mass on the text's centre rather
        than under it.
      */
      className="text-success-subtle-foreground block size-4 shrink-0"
      aria-label="Verified"
      role="img"
    />
  );
}

export function AttributeBankAccountEditor({
  label,
  account,
  accountName = false,
  verified = false,
  verificationNote = false,
  onDraft,
  onRevert,
  onCommit
}: {
  label: string;
  account: AttributeBankAccount | null;
  /** Offer the account-name part — see the `bankAccount` arm's own doc. */
  accountName?: boolean;
  /** Offer the verified flag. */
  verified?: boolean;
  /** Offer the verification note, beneath an answered-`yes` flag. */
  verificationNote?: boolean;
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
}) {
  const set = (patch: Partial<AttributeBankAccount>) => {
    onDraft(normalise({ ...(account ?? {}), ...patch }));
  };

  /*
    The note is revealed by the answer rather than disabled by it — the same
    choice `valueWithConfirmation` makes for its source fields, and for the same
    reason: a greyed-out control still occupies the eye and still asks to be
    clicked, while a revealed one arrives when it becomes relevant. Read from the
    **draft**, so it appears in the same gesture that ticks the flag.
  */
  const showNote = verificationNote && account?.isVerified === true;

  /*
    `Enter` commits and closes, `Escape` reverts, dismissing commits: the one
    contract all seven composite popover editors share (VALE-1321). See
    `./use-attribute-popover-commit`, which owns every subtlety in it.
  */
  const popoverCommit = useAttributePopoverCommit({
    draft: account,
    onRevert,
    onCommit
  });

  return (
    <Popover {...popoverCommit.popover}>
      <PopoverTrigger asChild>
        {/*
          Left-aligned and cell-width, matching the read-out it replaces — the
          trigger sits in the same slot, so the account must not jump sideways as
          the popover opens. Bare: the cell draws the fill and the ring (see
          `AttributeFieldControl`'s `unified` arm).
        */}
        <button
          type="button"
          aria-label={label}
          className={cn(
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_INSET,
            ATTRIBUTE_LEADING,
            'text-foreground min-w-0 flex-1 truncate rounded-md text-left',
            ATTRIBUTE_EDITOR_BARE
          )}
        >
          {formatBankAccountSummary(account) || attributePlaceholder(label)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={ATTRIBUTE_POPOVER_COLLISION_PADDING}
        className={cn(ATTRIBUTE_POPOVER_PANEL, ATTRIBUTE_COMPOSITE_PARTS)}
        {...popoverCommit.content}
      >
        {/*
          **The name first, then the two numbers.** It is the part a reader
          fills in from the top of a statement, and putting it above the numbers
          keeps the popover in the order the source document is read — the same
          order the flag-off Contacts form used.
        */}
        {accountName && (
          <div className={ATTRIBUTE_COMPOSITE_PART}>
            <Label
              htmlFor="attribute-bank-account-name"
              className={ATTRIBUTE_COMPOSITE_PART_LABEL}
            >
              Account name
            </Label>
            <Input
              id="attribute-bank-account-name"
              aria-label="Account name"
              autoFocus
              // Untrimmed, per §4.3 — trimming here strips a space the moment it
              // is typed, same as every other field in this treatment.
              value={account?.accountName ?? ''}
              onChange={(event) => set({ accountName: event.target.value })}
              className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
            />
          </div>
        )}

        <div className={ATTRIBUTE_COMPOSITE_PART}>
          <Label
            htmlFor="attribute-bank-account-bsb"
            className={ATTRIBUTE_COMPOSITE_PART_LABEL}
          >
            BSB
          </Label>
          <Input
            id="attribute-bank-account-bsb"
            aria-label="BSB"
            autoFocus={!accountName}
            inputMode="numeric"
            value={formatBsbInput(account?.bsb ?? '')}
            /*
              Formatted on the way in, unlike every other text part here. A BSB
              is six digits with a fixed separator and no legal free-text form,
              so there is nothing a reader can be mid-typing that the formatter
              would destroy — which is exactly what stops this being the
              trim-as-you-type mistake §4.3 bans.
            */
            onChange={(event) =>
              set({ bsb: formatBsbInput(event.target.value) })
            }
            className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
          />
        </div>

        <div className={ATTRIBUTE_COMPOSITE_PART}>
          <Label
            htmlFor="attribute-bank-account-number"
            className={ATTRIBUTE_COMPOSITE_PART_LABEL}
          >
            Account number
          </Label>
          <Input
            id="attribute-bank-account-number"
            aria-label="Account number"
            inputMode="numeric"
            value={account?.accountNumber ?? ''}
            onChange={(event) => set({ accountNumber: event.target.value })}
            className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
          />
        </div>

        {verified && (
          /*
            **The flag sits beside its label, not under it** (VALE-1247). Every
            other part here is a full-width input that needs the line it takes;
            a `Yes` / `No` pair is about 70px, so stacked it claimed a whole
            line of the popover to say two characters. See
            {@link ATTRIBUTE_COMPOSITE_PART_INLINE}.
          */
          <div className={ATTRIBUTE_COMPOSITE_PART_INLINE}>
            <Label className={ATTRIBUTE_COMPOSITE_PART_LABEL}>Verified</Label>
            <AttributeSegmentedBoolean
              value={account?.isVerified ?? false}
              ariaLabel="Verified"
              onValueChange={(next) =>
                set(
                  next
                    ? { isVerified: true }
                    : /*
                        Answering `No` drops the note with it — the same rule
                        `valueWithConfirmation` applies to its source fields. A
                        note left beside an unverified account is provenance for
                        a verification the record says did not happen, and the
                        next `Yes` would silently restore it.
                      */
                      { isVerified: false, verificationNote: null }
                )
              }
            />
          </div>
        )}

        {showNote && (
          <div className={ATTRIBUTE_COMPOSITE_PART}>
            <Label
              htmlFor="attribute-bank-account-verification-note"
              className={ATTRIBUTE_COMPOSITE_PART_LABEL}
            >
              Verification note
            </Label>
            <Textarea
              id="attribute-bank-account-verification-note"
              aria-label="Verification note"
              rows={3}
              value={account?.verificationNote ?? ''}
              onChange={(event) =>
                set({ verificationNote: event.target.value })
              }
              className={cn(ATTRIBUTE_VALUE_TEXT, 'py-1')}
            />
          </div>
        )}
        {popoverCommit.confirm}
      </PopoverContent>
    </Popover>
  );
}
